import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Plus, Search, Building2, RefreshCw, List, LayoutGrid, Users
} from "lucide-react";
import { toast } from "sonner";
import { useClient } from "@/context/ClientContext";

// Import extracted components
import { ClientCard, ClientTable, ClientFormModal, BulkAssignModal } from "@/components/clients";
import { devWarn } from "../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Clients = () => {
  const { refreshClients, selectClient, selectedClient } = useClient();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  
  // Multi-select state
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Assign modal state
  const [assignModal, setAssignModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    short_name: "",
    pan: "",
    gstin: "",
    contact_person: "",
    phone: "",
    email: "",
    address: "",
    tally_host: "localhost",
    tally_port: 9000,
    tally_company: ""
  });

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/clients`, { params });
      setClients(response.data);
    } catch (err) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.filter(u => u.role !== "admin"));
    } catch (err) {
      /* users list is supplementary for bulk assignment UI */
      devWarn("fetchUsers (non-critical):", err);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchUsers();
  }, [fetchClients, fetchUsers]);

  // Filtered clients based on search
  const filteredClients = clients.filter(client => {
    const searchLower = search.toLowerCase();
    return (
      client.name?.toLowerCase().includes(searchLower) ||
      client.short_name?.toLowerCase().includes(searchLower) ||
      client.pan?.toLowerCase().includes(searchLower) ||
      client.gstin?.toLowerCase().includes(searchLower)
    );
  });

  // Selection handlers
  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedClients(new Set(filteredClients.map(c => c.id)));
    } else {
      setSelectedClients(new Set());
    }
  };

  // CRUD handlers
  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error("Client name is required");
      return;
    }

    try {
      if (editingClient) {
        const response = await axios.put(`${API}/clients/${editingClient.id}`, formData);
        setClients(clients.map(c => c.id === editingClient.id ? response.data : c));
        toast.success("Client updated successfully");
      } else {
        const response = await axios.post(`${API}/clients`, formData);
        setClients([...clients, response.data]);
        toast.success("Client created successfully");
      }
      handleCloseDialog();
      refreshClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save client");
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      setClients(clients.filter(c => c.id !== clientId));
      setSelectedClients(prev => {
        const newSet = new Set(prev);
        newSet.delete(clientId);
        return newSet;
      });
      toast.success("Client deleted");
      refreshClients();
    } catch (err) {
      toast.error("Failed to delete client");
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || "",
      short_name: client.short_name || "",
      pan: client.pan || "",
      gstin: client.gstin || "",
      contact_person: client.contact_person || "",
      phone: client.phone || "",
      email: client.email || "",
      address: client.address || "",
      tally_host: client.tally_host || "localhost",
      tally_port: client.tally_port || 9000,
      tally_company: client.tally_company || ""
    });
    setDialogOpen(true);
  };

  const handleActivate = (client) => {
    selectClient(client);
    toast.success(`${client.name} is now active`);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingClient(null);
    setFormData({
      name: "",
      short_name: "",
      pan: "",
      gstin: "",
      contact_person: "",
      phone: "",
      email: "",
      address: "",
      tally_host: "localhost",
      tally_port: 9000,
      tally_company: ""
    });
  };

  // Bulk assign handler
  const handleBulkAssign = async () => {
    if (!selectedUserId || selectedClients.size === 0) {
      toast.error("Please select a user and at least one client");
      return;
    }

    try {
      await axios.post(
        `${API}/users/bulk-assign-clients`,
        {
          user_id: selectedUserId,
          client_ids: Array.from(selectedClients)
        }
      );

      toast.success(`${selectedClients.size} client(s) assigned successfully`);
      setAssignModal(false);
      setSelectedClients(new Set());
      setSelectAll(false);
      setSelectedUserId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign clients");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Clients
          </h1>
          <p className="text-slate-500 mt-1">Manage your client portfolio</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Toolbar */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="client-search-input"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === "grid" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                data-testid="grid-view-btn"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>

            {/* Refresh */}
            <Button variant="outline" size="sm" onClick={fetchClients} data-testid="refresh-clients-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>

            {/* Bulk Assign Button */}
            {selectedClients.size > 0 && (
              <Button 
                variant="outline" 
                onClick={() => setAssignModal(true)}
                data-testid="bulk-assign-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Assign {selectedClients.size} to User
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Clients Display */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {filteredClients.length} Client{filteredClients.length !== 1 ? 's' : ''}
            {selectedClients.size > 0 && (
              <span className="ml-2 text-emerald-600">({selectedClients.size} selected)</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No clients found. Add your first client to get started.</p>
            </div>
          ) : viewMode === "list" ? (
            <ClientTable
              clients={filteredClients}
              selectedClients={selectedClients}
              selectAll={selectAll}
              onSelectAll={handleSelectAll}
              onSelectClient={toggleClientSelection}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onActivate={handleActivate}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClients.map(client => (
                <ClientCard
                  key={client.id}
                  client={client}
                  isSelected={selectedClients.has(client.id)}
                  onSelect={toggleClientSelection}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onActivate={handleActivate}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Form Modal */}
      <ClientFormModal
        open={dialogOpen}
        onClose={handleCloseDialog}
        mode={editingClient ? 'edit' : 'create'}
        formData={formData}
        onFormChange={setFormData}
        onSubmit={handleSubmit}
      />

      {/* Bulk Assign Modal */}
      <BulkAssignModal
        open={assignModal}
        onClose={() => {
          setAssignModal(false);
          setSelectedUserId("");
        }}
        selectedCount={selectedClients.size}
        users={users}
        selectedUserId={selectedUserId}
        onUserChange={setSelectedUserId}
        onAssign={handleBulkAssign}
      />
    </div>
  );
};

export default Clients;
